import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type FeedbackKind } from "../lib/api";
import { OPEN_FEEDBACK_EVENT, type FeedbackPrefill } from "../lib/feedback";
import { usePathname } from "../lib/router-context";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import { Button } from "./ui/Button";

const DISCORD_URL = "https://discord.gg/GvqXjxc3xx";
const HIDDEN_KEY = "wikiparty:feedback-button-hidden";
const KINDS: FeedbackKind[] = ["feedback", "bug"];

function useFeedbackStyles() {
  const isDark = useAppStore((state) => state.isDark);
  const theme = getThemeColors(isDark);
  return {
    isDark,
    theme,
    panel: `${theme.bgSecondary} ${theme.cardBorder} rounded-2xl shadow-2xl`,
    fab: `${theme.buttonPrimary} ${theme.buttonPrimaryText} rounded-full shadow-lg font-medium`,
    chip: `rounded-full border ${theme.border} ${theme.textSecondary} px-3 py-1 text-sm`,
    chipActive: `rounded-full border border-transparent ${theme.bgAccent} ${theme.buttonPrimaryText} px-3 py-1 text-sm`,
    field: `w-full resize-y rounded-xl border ${theme.border} ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder} px-3 py-2 text-sm`,
    code: `max-h-32 overflow-auto whitespace-pre-wrap rounded-lg ${theme.accentBg} ${theme.textSecondary} p-2 text-xs`,
  };
}

function readHidden() {
  try {
    return localStorage.getItem(HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(HIDDEN_KEY, "1");
    else localStorage.removeItem(HIDDEN_KEY);
  } catch {
    // Private mode or blocked storage: the choice lasts until the page reloads.
  }
}

function FeedbackForm({
  prefill,
  page,
  onSent,
}: {
  prefill: FeedbackPrefill;
  page: string;
  onSent: () => void;
}) {
  const { t, i18n } = useTranslation();
  const styles = useFeedbackStyles();
  const name = useAppStore((state) => state.auth?.name);
  const id = useId();
  const [kind, setKind] = useState<FeedbackKind>(prefill.kind ?? "feedback");
  const [message, setMessage] = useState(prefill.message ?? "");
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setStatus("sending");
    setError(null);
    try {
      await api.sendFeedback({
        kind,
        message: message.trim(),
        code: prefill.code,
        context: { ...prefill.context, Language: i18n.language },
        page,
      });
      setStatus("sent");
      onSent();
    } catch (e) {
      setError((e as Error).message);
      setStatus("idle");
    }
  }

  if (status === "sent") {
    return (
      <div
        role="status"
        data-testid="feedback-sent"
        className="flex flex-col gap-1 py-2"
      >
        <strong className={styles.theme.text}>
          {t("components.feedback.sentTitle")}
        </strong>
        <span className={`${styles.theme.textSecondary} text-sm`}>
          {t("components.feedback.sentBody")}
        </span>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <div
        className="flex flex-wrap gap-1.5"
        role="radiogroup"
        aria-label={t("components.feedback.kindsLabel")}
      >
        {KINDS.map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={kind === value}
            data-testid={`feedback-kind-${value}`}
            className={`cursor-pointer ${kind === value ? styles.chipActive : styles.chip}`}
            onClick={() => setKind(value)}
          >
            {t(`components.feedback.kinds.${value}`)}
          </button>
        ))}
      </div>
      {prefill.code && <pre className={styles.code}>{prefill.code}</pre>}
      <label className="sr-only" htmlFor={id}>
        {t("components.feedback.message")}
      </label>
      <textarea
        id={id}
        rows={4}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        maxLength={2000}
        required={!prefill.code}
        placeholder={t(
          kind === "bug"
            ? "components.feedback.placeholderBug"
            : "components.feedback.placeholderFeedback",
        )}
        className={styles.field}
        data-testid="feedback-message"
        data-autofocus
      />
      {error && (
        <p role="alert" className={`${styles.theme.errorText} text-sm`}>
          {error}
        </p>
      )}
      <Button
        type="submit"
        variant="primary"
        isDark={styles.isDark}
        className="py-3"
        disabled={status === "sending"}
        data-testid="feedback-send-btn"
      >
        {t(
          status === "sending"
            ? "components.feedback.sending"
            : "components.feedback.send",
        )}
      </Button>
      <p className={`${styles.theme.textMuted} text-xs`}>
        {name
          ? t("components.feedback.sentAs", { name })
          : t("components.feedback.anonymous")}{" "}
        {t("components.feedback.ratherChat")}{" "}
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          {t("components.feedback.discord")}
        </a>
      </p>
    </form>
  );
}

/** The Feedback button in the corner of every page and the small panel it opens. */
export function FeedbackWidget() {
  const { t } = useTranslation();
  const styles = useFeedbackStyles();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  // A new key starts a fresh form; closing keeps an unsent draft.
  const [form, setForm] = useState<{ key: number; prefill: FeedbackPrefill }>({
    key: 0,
    prefill: {},
  });
  const [sentKey, setSentKey] = useState(-1);
  const [hidden, setHidden] = useState(readHidden);
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const prefill = (event as CustomEvent<FeedbackPrefill>).detail ?? {};
      setForm((f) => ({ key: f.key + 1, prefill }));
      setOpen(true);
    };
    window.addEventListener(OPEN_FEEDBACK_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      // The trigger is excluded, or its click would close and reopen the panel.
      if (
        !panel.current?.contains(target) &&
        !trigger.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
    };
  }, [open, form.key]);

  const showButton = !hidden;

  return (
    <>
      {/* Hidden, not unmounted, so an unsent draft survives closing. */}
      <div
        ref={panel}
        hidden={!open}
        role="dialog"
        aria-label={t("components.feedback.title")}
        data-testid="feedback-panel"
        className={`feedback-in fixed inset-x-2 z-50 flex max-h-[calc(100dvh-110px)] flex-col gap-3 overflow-y-auto p-4 sm:inset-x-auto sm:right-5 sm:w-[380px] ${showButton ? "bottom-16 sm:bottom-20" : "bottom-3 sm:bottom-5"} ${styles.panel}`}
      >
        <div className="flex items-center justify-between">
          <strong className={`${styles.theme.text} text-base`}>
            {t("components.feedback.title")}
          </strong>
          <button
            type="button"
            aria-label={t("components.feedback.close")}
            data-testid="feedback-close-btn"
            className={`${styles.theme.textMuted} cursor-pointer px-1 text-2xl leading-none`}
            onClick={() => setOpen(false)}
          >
            ×
          </button>
        </div>
        <FeedbackForm
          key={form.key}
          prefill={form.prefill}
          page={pathname}
          onSent={() => setSentKey(form.key)}
        />
        <button
          type="button"
          data-testid="feedback-visibility-btn"
          className={`${styles.theme.textMuted} cursor-pointer self-center p-1 text-xs hover:underline`}
          onClick={() => {
            setHidden(!hidden);
            writeHidden(!hidden);
            if (!hidden) setOpen(false);
          }}
        >
          {t(hidden ? "components.feedback.show" : "components.feedback.hide")}
        </button>
      </div>
      {showButton && (
        <button
          ref={trigger}
          type="button"
          aria-expanded={open}
          data-testid="feedback-fab"
          className={`feedback-in fixed bottom-3 right-3 z-50 inline-flex min-h-10 cursor-pointer items-center gap-2 px-4 text-sm sm:bottom-5 sm:right-5 sm:min-h-11 sm:px-5 ${styles.fab}`}
          onClick={() => {
            // After a sent message, the next open starts a fresh form.
            if (!open && sentKey === form.key) {
              setForm((f) => ({ key: f.key + 1, prefill: {} }));
            }
            setOpen(!open);
          }}
        >
          <span aria-hidden="true">💬</span>
          {t("components.feedback.button")}
        </button>
      )}
    </>
  );
}
